<template>
  <div class="w-full min-w-[288px]">
    <div class="flex flex-col p-3">
      <div class="flex justify-between gap-1">
        <div class="inline-flex items-center justify-start">
          <SymbolTradeStateIcon
            class="me-2"
            size="medium"
            :trading-state-code="order.instrumentTradingState"
          />
          <Retail v-if="getSymbolIsin(order)" :isin="getSymbolIsin(order)">
            <NText variant="h3" tag="h3" weight="bold">
              {{ order.instrumentName }}
            </NText>
          </Retail>
        </div>

        <div class="flex items-center">
          <NumberFormatter
            #="{ number }"
            :value="(order.referencePriceVariationPercentage ?? 0) / 100"
            maximum-fraction-digits="2"
            sign-display="always"
            number-style="percent"
          >
            <NText
              weight="bold"
              variant="body"
              dir="ltr"
              :class="{
                'text-brand-primary':
                  (order.referencePriceVariationPercentage ?? 0) > 0,
                'text-critical-primary':
                  (order.referencePriceVariationPercentage ?? 0) < 0,
              }"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="order.lastTradePrice">
            <NText
              :key="number"
              variant="h1"
              tag="h1"
              weight="bold"
              class="ms-1"
            >
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </div>

      <NText variant="caption1" class="text-neutral-secondary">
        {{ order.instrumentTitle }}
      </NText>
    </div>

    <div class="bg-neutral-secondary px-4 mt-2 rounded-md">
      <div v-if="isOption" class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderType') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ t('search.symbol_groups.optionTrading') }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.orderSide') }}
        </NText>

        <NText
          v-if="order.orderSideId === ORDER_SIDES.buy"
          variant="caption1"
          weight="medium"
          class="pr-2 text-brand-primary"
        >
          {{ t('common.buy') }}
        </NText>

        <NText
          v-if="order.orderSideId === ORDER_SIDES.sell"
          variant="caption1"
          weight="medium"
          class="pr-2 text-critical-primary"
        >
          {{ t('common.sell') }}
        </NText>
      </div>

      <div
        class="flex justify-between items-center py-2 border-b border-neutral-tertiary"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_state') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ order.orderState }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('misc.quantity') }} ({{ t('modules.order.executed') }})
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="order.executedQuantity">
            <NText variant="caption1" weight="medium"> ({{ number }}) </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="order.quantity">
            <NText variant="caption1" weight="medium">{{ number }}</NText>
          </NumberFormatter>
        </div>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_price') }}
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="order.price">
            <NText variant="caption1" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary pr-1">
            {{ t('misc.rial') }}
          </NText>
        </div>
      </div>

      <div
        :class="[
          'flex justify-between items-center py-2',
          {
            'border-b border-neutral-tertiary': !isOption,
          },
        ]"
      >
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.order_value') }}
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="totalValue" round>
            <NText
              :key="number"
              variant="caption1"
              weight="medium"
              class="mx-1"
            >
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText variant="overline" class="text-neutral-secondary">
            {{ t('misc.rial') }}
          </NText>
        </div>
      </div>

      <div
        v-if="isOption && order.marginSource"
        :class="[
          'flex justify-between items-center py-2',
          {
            'border-b border-neutral-tertiary': isOption,
          },
        ]"
      >
        <NText variant="caption1" weight="medium">
          {{ t('option.guaranteeMethod') }}
        </NText>

        <NText
          class="text-neutral-secondary"
          variant="caption1"
          weight="medium"
        >
          {{ GUARANTEE_METHOD[order.marginSource] }}
          {{ order.marginSourceAssets?.[0]?.name }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('modules.order.creationTime') }}
        </NText>

        <NText variant="caption1" weight="medium" class="pr-2">
          {{ creationTime }}
        </NText>
      </div>

      <div class="flex justify-between items-center py-2">
        <NText variant="caption1" weight="medium">
          {{ t('orders.valid_until') }}
        </NText>

        <NText
          v-if="order.validityTypeId === VALIDITY_TYPES.GoodTillDate"
          variant="caption1"
          weight="medium"
        >
          {{ d(order.validityDate, 'shortDate') }}
        </NText>

        <NText v-else variant="caption1" weight="medium">
          {{
            order.validityTypeId === VALIDITY_TYPES.Day
              ? validityDayTitle
              : t('common.cancellation')
          }}
        </NText>
      </div>

      <div v-if="order.errorDescription" class="flex justify-between py-3">
        <NText
          variant="caption1"
          weight="medium"
          class="text-neutral-secondary-hover"
        >
          {{ t('modules.order.description') }}
        </NText>
        <NText variant="caption1" weight="bold" class="mr-4">
          {{ order.errorDescription }}
        </NText>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  calcSymbolBuyValue,
  calcSymbolSellValue,
  getSymbolIsin,
  useSymbolDeductions,
  isOptionSymbol,
} from '@core/symbol';
import { useI18n } from 'vue-i18n';
import { OrderHistoryItem } from '@libs/api-reference';
import { computed } from 'vue';
import { GUARANTEE_METHOD } from '@core/option';
import { ORDER_SIDES, VALIDITY_TYPES } from '@core/order';

import { Retail, SymbolTradeStateIcon } from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';

type OrderHistoryDialogProps = {
  order: OrderHistoryItem;
};

const props = defineProps<OrderHistoryDialogProps>();

const { findWageConfigBySymbol } = useSymbolDeductions();

const { t, d } = useI18n();

const creationTime = computed(
  () => `${d(props.order.entryDate, 'shortDate')} ,
    ${d(props.order.entryDate, 'shortTime')}`
);

const totalValue = computed(() => {
  if (props.order.value) return props.order?.value;

  const wage = findWageConfigBySymbol(props.order);

  return props.order.orderSideId === ORDER_SIDES.buy
    ? calcSymbolBuyValue(props.order, wage)
    : calcSymbolSellValue(props.order, wage);
});

const validityDayTitle = computed(() => {
  const creationDay = new Date(props.order.entryDate).getDate();
  const today = new Date().getDate();

  return creationDay === today
    ? t('misc.today')
    : d(props.order.entryDate, 'shortDate');
});

const isOption = computed(() => isOptionSymbol(props.order.instrumentTypeCode));
</script>
